import { FC, useCallback, useState } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { InsuranceTabStore } from '@frontend/insurance-tab';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { theme } from '@frontend/theme';
import { Button, Heading, Text } from '@frontend/design-system';

type InsurancePayerConnectionCitationProps = {
  locationId: string;
  websiteId?: string;
  payerId?: string;
};

export const InsurancePayerConnectionCitation: FC<InsurancePayerConnectionCitationProps> = ({
  websiteId,
  payerId,
  locationId,
}) => {
  const [isCitationSelected, setIsCitationSelected] = useState(false);
  const { t } = useTranslation('task-center');
  const { navigate } = useSettingsNavigate();

  const { setPayerConnectionPayerID, setPayerConnectionPayerWebsiteID } =
    InsuranceTabStore.useInsurancePayerConnectionShallowStore(
      'setPayerConnectionPayerID',
      'setPayerConnectionPayerWebsiteID'
    );

  const goToPayerConnection = useCallback(() => {
    setPayerConnectionPayerID(payerId || '');
    setPayerConnectionPayerWebsiteID(websiteId || '');
    navigate({ to: `/insurance/payer-connection-management/:locationId`, params: { locationId } });
  }, [payerId, websiteId, locationId, setPayerConnectionPayerID, setPayerConnectionPayerWebsiteID, navigate]);

  if (!payerId || !websiteId) {
    return null;
  }

  return (
    <section
      css={insurancePayerConnectionCitationContainerStyle(isCitationSelected)}
      onClick={() => setIsCitationSelected(true)}
      data-tracking-id='insurance-payer-connection-citation'
    >
      <div css={headerStyles}>
        <Icon name='insurance-error-small' size={24} />
        <Heading level={3}>{t('Payer Connection Failure')}</Heading>
      </div>
      <div css={descriptionStyle}>
        <Text>{t('Payer : {{payerId}}', { payerId })}</Text>
        <Text>{t('Website : {{websiteId}}', { websiteId })}</Text>
      </div>
      <div css={buttonContainerStyle}>
        <Button
          trackingId='task-center-reconnect-payer-button'
          onClick={goToPayerConnection}
          type='button'
          variant='tertiary'
        >
          {t('Reconnect Payer')}
        </Button>
      </div>
    </section>
  );
};

const buttonContainerStyle = css({
  display: 'flex',
  justifyContent: 'flex-end',
  marginTop: theme.spacing(1),
});

const descriptionStyle = css({
  whiteSpace: 'pre-line',
  marginTop: theme.spacing(2),
  backgroundColor: theme.colors.neutral5,
  padding: theme.spacing(2),
  borderRadius: theme.borderRadius.small,
});

const headerStyles = css({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
});

const insurancePayerConnectionCitationContainerStyle = (isCitationSelected: boolean) =>
  css({
    border: `1px solid ${isCitationSelected ? theme.colors.primary50 : theme.colors.neutral10}`,
    borderRadius: theme.borderRadius.medium,
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
    cursor: 'pointer',
    ':hover': {
      boxShadow: theme.shadows.light,
    },
  });
